import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, AuthContextType, useFullPortalEnabled } from "../utilities/customHooks";
import { useSuspenseQuery } from "@tanstack/react-query";
import { getRegionStatsAsync } from "../services/adminService";
import { queryKeys } from "../utilities/queryKeys";
import { getEffectiveCapabilities } from "../utilities/regionCapabilities";
import { getEffectiveVisibleRegions } from "../utilities/regionVisibility";
import { Card, CardContent } from "./ui/card";
import {
  AlertTriangle,
  Clock,
  Info,
  Users,
  UserCheck,
  ClipboardCheck,
  FileText,
  FilePlus,
  ShieldCheck,
  PenLine,
  CheckCircle2,
  Receipt,
  type LucideIcon
} from "lucide-react";

type SeverityTier = "urgent" | "attention" | "info" | "blue" | "orange" | "neutral";

interface StatCardConfig {
  key: string;
  label: string;
  value: number;
  // Omit to render a non-clickable, informational-only card (no drill-down).
  route?: string;
  tier: SeverityTier;
  icon: LucideIcon;
  fullPortalOnly?: boolean;
}

const tierStyles: Record<SeverityTier, { bg: string; iconColor: string }> = {
  urgent: { bg: "bg-cDenied", iconColor: "text-cError" },
  attention: { bg: "bg-cLightYellow", iconColor: "text-amber-600" },
  info: { bg: "bg-cLightGreen", iconColor: "text-cSuccess" },
  blue: { bg: "bg-blue-50", iconColor: "text-blue-600" },
  orange: { bg: "bg-orange-50", iconColor: "text-orange-600" },
  neutral: { bg: "bg-cLightGrey", iconColor: "text-muted-foreground" },
};

function StatCard({ config }: { config: StatCardConfig }) {
  const navigate = useNavigate();
  const isActive = config.value > 0;
  const style = tierStyles[config.tier];
  const Icon = config.icon;
  const clickable = !!config.route;

  return (
    <Card
      className={`transition-colors duration-150 ${clickable ? "cursor-pointer hover:shadow-md" : ""} ${isActive ? style.bg : ""}`}
      onClick={clickable ? () => navigate(config.route!) : undefined}
    >
      <CardContent className="p-4 flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${isActive ? style.iconColor : "text-muted-foreground"}`} />
          <span className={`text-2xl font-bold ${clickable ? "hover:underline" : ""}`}>{config.value}</span>
        </div>
        <p className="text-xs text-muted-foreground text-center leading-tight">{config.label}</p>
      </CardContent>
    </Card>
  );
}

const AdminDashboardStats = () => {
  const { userRegion, userRole } = useContext(AuthContext) as AuthContextType;
  const fullPortalEnabled = useFullPortalEnabled();
  const capabilities = getEffectiveCapabilities(userRegion, fullPortalEnabled);
  const { data: founderDetailsData } = useSuspenseQuery({
    queryKey: queryKeys.dashboards.adminStats(),
    queryFn: () => getRegionStatsAsync(),
  });

  const r = founderDetailsData.regionalDetails;

  // The stat counts are scoped to the caller's own region(s) (superAdmins are
  // clamped to their home region). Mirror that scope onto the deep-links so a
  // card opens the destination list pre-filtered to exactly what it counted.
  // Only pass a region filter when the caller maps to a SINGLE region — a
  // multi-region admin (IO → IO+LL+SC) is counted across all of them, so
  // clamping to one would under-show the list.
  const visibleRegions = userRole ? getEffectiveVisibleRegions(userRole, userRegion || "") : null;
  const dashRegions = visibleRegions === null ? (userRegion ? [userRegion] : []) : visibleRegions;
  const regionParam = dashRegions.length === 1 ? dashRegions[0] : null;
  const withRegion = (path: string) =>
    regionParam ? `${path}${path.includes("?") ? "&" : "?"}region=${encodeURIComponent(regionParam)}` : path;
  // `stage` = SoW-stage display name (matched by AllSowsList); `phase` = stage id
  // (resolved to backend statuses by MembersList).
  const sowStageRoute = (stageDisplayName: string) => withRegion(`/dashboard/all-sows?stage=${encodeURIComponent(stageDisplayName)}`);
  const membersPhaseRoute = (stageId: string) => withRegion(`/dashboard/memberships?phase=${stageId}`);

  // NOTE: order is tuned so the two red "AO review" cards land in the two centre
  // columns of a row in the 4-column (lg) layout (array indices 5 & 6).
  const cards: StatCardConfig[] = [
    { key: "awaitingReview", label: "new applications awaiting review", value: r.awaitingReview, route: "/dashboard/pending-applications", tier: "blue", icon: AlertTriangle },
    { key: "ipaInviteSent", label: "IPA invite sent", value: r.ipaInviteSent, route: membersPhaseRoute("ipa-invite-sent"), tier: "orange", icon: UserCheck, fullPortalOnly: true },
    { key: "ipaAssessmentReady", label: "IPA Assessment Ready", value: r.ipaAssessmentReady, route: "/dashboard/admin-expert-recommendations", tier: "orange", icon: ClipboardCheck, fullPortalOnly: true },
    { key: "awaitingGcAssessment", label: "Awaiting GC Assessment", value: r.awaitingGcAssessment, route: membersPhaseRoute("awaiting-gc-assessment"), tier: "orange", icon: Clock, fullPortalOnly: true },
    { key: "gcAssessmentReady", label: "GC Assessment Ready", value: r.gcAssessmentReady, route: membersPhaseRoute("gc-assessment-ready"), tier: "orange", icon: ClipboardCheck, fullPortalOnly: true },
    { key: "sowAwaitingAoReview", label: "SoW Awaiting AO review", value: r.sowAwaitingAoReview, route: sowStageRoute("SoW Awaiting AO Review"), tier: "urgent", icon: FileText, fullPortalOnly: true },
    { key: "invoicesAwaitingReview", label: "Invoices Awaiting AO Review", value: r.invoicesAwaitingReview, route: "/dashboard/admin-invoices?status=awaitingReview", tier: "urgent", icon: Receipt, fullPortalOnly: true },
    { key: "sowRequested", label: "SoW requested", value: r.sowRequested, route: sowStageRoute("SoW Requested"), tier: "info", icon: FilePlus, fullPortalOnly: true },
    { key: "sowAwaitingIpaReview", label: "SoW Awaiting IPA review", value: r.sowAwaitingIpaReview, route: sowStageRoute("SoW Awaiting IPA review"), tier: "orange", icon: FileText, fullPortalOnly: true },
    { key: "sowReadyForAudit", label: "SoW Ready for Audit", value: r.sowReadyForAudit, route: sowStageRoute("SoW Ready for Audit"), tier: "orange", icon: ShieldCheck, fullPortalOnly: true },
    { key: "sowReadyForSignature", label: "SoW Ready for Signature", value: r.sowReadyForSignature, route: sowStageRoute("SoW Ready for Signature"), tier: "attention", icon: PenLine, fullPortalOnly: true },
    { key: "sowSigned", label: "SoW Signed", value: r.sowSigned, route: sowStageRoute("SoW Signed"), tier: "info", icon: CheckCircle2, fullPortalOnly: true },
    { key: "projectCompleted", label: "Project Completed", value: r.projectCompleted, route: membersPhaseRoute("project-completed"), tier: "info", icon: CheckCircle2, fullPortalOnly: true },
    // Non-clickable: this counts all registered founder accounts, which is a
    // different population from the /dashboard/startups list (founders with a
    // submitted application), so a drill-down would read as a 190≠175 mismatch.
    { key: "allStartup", label: "total registered", value: r.allStartup, tier: "neutral", icon: Users },
    { key: "unsubmitted", label: "unsubmitted applications", value: r.unsubmitted, route: "/dashboard/founders-no-profile", tier: "neutral", icon: Info },
  ];

  const visibleCards = cards.filter(c => !c.fullPortalOnly || capabilities.hasFullPortal);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {visibleCards.map(card => (
        <StatCard key={card.key} config={card} />
      ))}
    </div>
  );
};

export default AdminDashboardStats;
